/** @jsxImportSource ../../i18n */
import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { usePrefersReducedMotion } from '../../theme/motion';
import { formatMoney, formatPercent } from '../../theme/format';
import { Money, MASKED_AMOUNT } from '../ui/Money';
import { ChartTooltip } from './ChartTooltip';
import { chartColors, seriesColor } from './chartTheme';

export const withColors = (items) => items.map((item, i) => ({ ...item, color: item.color || seriesColor(i) }));

export const AllocationDonut = ({ data, total, centerLabel = 'Valeur totale', height = 220, showLegend = true, legendLimit = 7, className }) => {
  const { hidden } = usePrivacy();
  const reduced = usePrefersReducedMotion();
  const sum = total ?? data.reduce((acc, d) => acc + d.value, 0);
  const share = (v) => (sum > 0 ? (v / sum) * 100 : 0);
  return (
    <div className={className}>
      <div className="relative" style={{ height }}>
        {/* The legend below carries the same data as text. */}
        <div className="h-full" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" rootTabIndex={-1} innerRadius="72%" outerRadius="100%" paddingAngle={1.5} stroke={chartColors.canvas} strokeWidth={2} isAnimationActive={!reduced} animationDuration={700} startAngle={90} endAngle={-270}>
                {data.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
              </Pie>
              <Tooltip content={<ChartTooltip nameFormatter={(name) => name} valueFormatter={(v) => (hidden ? formatPercent(share(v), { sign: false }) : `${formatMoney(v, { decimals: 0 })} · ${formatPercent(share(v), { sign: false })}`)} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-fg-subtle">{centerLabel}</span>
          <span className="num mt-1 font-display text-xl text-fg">{hidden ? MASKED_AMOUNT : formatMoney(sum, { compact: true })}</span>
        </div>
      </div>
      {showLegend && (
        <ul className="mt-5 space-y-2">
          {data.slice(0, legendLimit).map((item) => (
            <li key={item.name} className="flex items-center gap-2.5 text-[13px]">
              <span aria-hidden="true" className="h-2.5 w-2.5 flex-shrink-0 rounded-sm" style={{ backgroundColor: item.color }} />
              <span className="min-w-0 flex-1 truncate text-fg-body">{item.name}</span>
              <Money value={item.value} decimals={0} className="text-fg-subtle" />
              <span className="num w-12 text-right font-medium text-fg">{formatPercent(share(item.value), { sign: false })}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
